import React, { useEffect } from 'react';

import {
    Heading,
    Container,
    Row,
    Col,
    Text,
    className,
    Accordion,
    isMobile,
} from 'aplazame-design-system';
import { useTranslate } from 'soluble-gatsby-source';

import BlogMetasComponent from '../BlogMetasComponent';
import SocialIconsComponent from '../SocialIconsComponent';
import BlogItemComponent from '../BlogItemComponent';
import HtmlComponent from '../HtmlComponent';

import style from './style.module.scss';
import textStyle from 'aplazame-design-system/src/scss/text.module.scss';
import transitionStyle from 'aplazame-design-system/src/scss/transitions.module.scss';

function SharingIcons({ title, pageUrl, wrapperClassName }) {
    return (
        <SocialIconsComponent
            wrapperClassName={wrapperClassName}
            twitterUrl={`https://twitter.com/intent/tweet?url=${encodeURI(
                pageUrl
            )}&text=${encodeURI(title)}`}
            facebookUrl={`https://www.facebook.com/sharer/sharer.php?u=${encodeURI(
                pageUrl
            )}`}
            linkedinUrl={`http://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(
                pageUrl
            )}&title=${encodeURI(title)}`}
        />
    );
}

export default function BlogPostComponent({
    title,
    content,
    categoryPages,
    date,
    by,
    authorPage,
    pageUrl,
    relatedPosts,
    tableOfContents,
}) {
    const translation = useTranslate();

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.document.body.dataset.hasSticky = 'true';
    }, []);

    return (
        <section>
            <Container pb={{ xs: 20 }}>
                <Row>
                    <Col
                        col={{ lg: 6 }}
                        offset={{ lg: 3 }}
                        pb={{ xs: 10 }}
                        data-sal="slide-up"
                        data-sal-easing="ease-out-quart"
                        style={{
                            '--sal-delay': `300ms`,
                        }}
                    >
                        <BlogMetasComponent
                            categoryPages={categoryPages}
                            date={date}
                            mt={{
                                xs: 2,
                            }}
                            mb={{
                                xs: 5,
                            }}
                        />
                        <Heading
                            tag="h1"
                            className={`h2 ${textStyle.gradual} ${style.heading}`}
                        >
                            {title}
                        </Heading>
                        <Text
                            tag="span"
                            className={`body d-block mt-2 ${transitionStyle['child-link']} ${style.metas}`}
                        >
                            {authorPage
                                ? translation[
                                      'wordpress-posts-section.blog-item.by'
                                  ].text.replace(
                                      '{author}',
                                      `<a class="author-link" href="/${authorPage.slug}/">${authorPage.title}</a>`
                                  )
                                : by}
                        </Text>
                    </Col>
                </Row>
                <Row>
                    <Col col={{ lg: 3 }}>
                        <div {...className(style['sidebar-sticky'])}>
                            {tableOfContents && tableOfContents.length ? (
                                <Accordion
                                    initialShow={isMobile() ? null : 0}
                                    wrapperClassName={`${style.accordion}`}
                                    titleClassName={`${style['accordion__title']} ${transitionStyle.link} d-lg-none`}
                                    items={[
                                        {
                                            title:
                                                translation[
                                                    'blog-post-page.table-of-contents.index'
                                                ].text,
                                            body: (
                                                <>
                                                    {tableOfContents.map(
                                                        (c, i) => (
                                                            <a
                                                                key={i}
                                                                className={`${style['accordion__item']} ${transitionStyle.link}`}
                                                                href={`#${c.slug}`}
                                                            >
                                                                {c.title}
                                                            </a>
                                                        )
                                                    )}
                                                </>
                                            ),
                                        },
                                    ]}
                                />
                            ) : null}
                            <SharingIcons
                                wrapperClassName="d-none d-lg-block"
                                title={title}
                                pageUrl={pageUrl}
                            />
                        </div>
                    </Col>
                    <Col col={{ lg: 6 }}>
                        <HtmlComponent type="blog-post" html={content} />
                        <Heading
                            tag="h5"
                            className="h4 font-weight-bold mt-5 mt-lg-10 mb-1"
                        >
                            {translation['blog-post-page.sharing.title'].text}
                        </Heading>
                        <SharingIcons
                            wrapperClassName="mb-0"
                            title={title}
                            pageUrl={pageUrl}
                        />
                    </Col>
                    {relatedPosts && relatedPosts.length ? (
                        <Col col={{ lg: 3 }} pt={{ xs: 10, lg: 0 }}>
                            <Heading
                                tag="h5"
                                className="body font-weight-bold mb-5"
                            >
                                {
                                    translation['blog-post-page.related.title']
                                        .text
                                }
                            </Heading>
                            {relatedPosts.map((r, i) => (
                                <BlogItemComponent
                                    key={i}
                                    minimal={true}
                                    wrapperClassName={`${style.related} mb-${
                                        i === relatedPosts.length - 1 ? 0 : 3
                                    }`}
                                    buttonClassName={style['related__button']}
                                    {...r}
                                />
                            ))}
                        </Col>
                    ) : null}
                </Row>
            </Container>
        </section>
    );
}
